"use client";

import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import TestimonialCard from "./TestimonialCard";
import { TestimonialsSectionQueryResult } from "@/sanity.types";
import { urlFor } from "@/sanity/lib/image";
import Image from "next/image";
import clsx from "clsx";

type TestimonialSlider = {
	testimonials: NonNullable<TestimonialsSectionQueryResult>["testimonials"];
};

export default function TestimonialSlider({ testimonials }: TestimonialSlider) {
	const [emblaRef] = useEmblaCarousel(
		{
			loop: false,
			align: "start",
			containScroll: "trimSnaps",
			skipSnaps: false,
			slidesToScroll: 1,
		},
		[Autoplay({ delay: 4000, stopOnInteraction: false })],
	);

	if (!testimonials || testimonials.length === 0) {
		return null;
	}

	return (
		<div className="embla group relative">
			<div className="embla__viewport overflow-hidden" ref={emblaRef}>
				<div className="embla__container mx-3.5 flex">
					{testimonials.map((testimonial, index) => {
						// 3rd testimonial (index 2) gets special styling
						const isSpecial = index === 2;
						const fillColor = isSpecial ? "#0B304A" : "#FFF";

						return (
							<div
								key={testimonial._key}
								className="embla__slide mr-[0.438rem] flex-shrink-0 last:mr-0"
								style={{
									flex: "0 0 auto",
									width: "auto",
								}}
							>
								<div className="xs2:max-w-[50vw] relative h-[24.375rem] w-auto max-w-[77.5vw]">
									<TestimonialCard fillColor={fillColor} specialTestimonial={isSpecial} className="h-full w-full">
										<div className="flex h-full flex-col">
											<p
												className={clsx("text-body-s sm:text-body-m mb-10 whitespace-pre-wrap", {
													"text-navy-blue-middle": isSpecial,
													"text-grey-200": !isSpecial,
												})}
											>
												{testimonial.opinion}
											</p>
											<div className="absolute -bottom-8 mt-auto flex items-center gap-x-2.5">
												{testimonial.image?.asset && (
													<div className="relative h-10 w-10">
														<Image
															src={urlFor(testimonial.image).url()}
															alt={testimonial.image.alt || `${testimonial.authorName} photo`}
															fill
															className="rounded-sm object-cover"
															sizes="10vw"
														/>
													</div>
												)}
												<div>
													<p
														className={clsx("text-cap-s mb-[0.3rem]", {
															"text-white": isSpecial,
															"text-graphite-black": !isSpecial,
														})}
													>
														{testimonial.authorName}
													</p>
													<p
														className={clsx("text-cap-s", {
															"text-navy-blue-middle": isSpecial,
															"text-grey-300": !isSpecial,
														})}
													>
														{testimonial.authorPosition}
													</p>
												</div>
											</div>
										</div>
									</TestimonialCard>
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}
